import JSZip from "jszip"
import { TFunction } from "i18next"

import { SECTION_TYPE, REPORT_TYPE } from "@/app/report/common/constants"
import { buildCustomTableCSV } from "@/app/report/common/export/customTableCsv"
import type { PositionRankFn } from "@/app/report/common/export/sortRows"
import { triggerBlobDownload } from "@/app/report/common/export/exportTable"
import { MetricMapType, StatsSettings } from "@/app/report/common/type"
import { TrendGroupSummaryRow } from "@/types/report"
import { CalculatedMetric, Unit } from "@/types/setting"

/** Excel UTF-8 호환용 BOM */
const BOM = "﻿"

export type BatchCSVFile = { fileName: string; content: string }

/** 표 섹션(customTable) 명세. Stats 섹션은 폐지되어(#99) 템플릿 로드 시 customTable 로 대치된 뒤 여기로 온다. */
export type StatsSectionSpec = { sectionType: string; title: string; statsSettings: StatsSettings }

/** Custom Table 의 그룹 집계(/summary/trend/group 또는 세션 모드 /summary/daily/group). groupKey 는 섹션 settings 에서 취한다. */
export type DailyGroupSummaryFetcher = (
  idfActivity: number,
  settings: StatsSettings,
) => Promise<TrendGroupSummaryRow[]>

export type PositionRankFns = {
  positionRank: PositionRankFn
  positionGroupRank: (group?: string) => number
}

// --- 내부 유틸 (테스트 위해 export) ---

export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let cursor = 0

  async function worker() {
    while (cursor < items.length) {
      const index = cursor++
      results[index] = await fn(items[index], index)
    }
  }

  const workerCount = Math.max(1, Math.min(limit, items.length))
  await Promise.all(Array.from({ length: workerCount }, () => worker()))

  return results
}

/** 파일/디렉토리명으로 쓸 수 없는 문자 제거 */
export function sanitizeFileName(name: string): string {
  return name.replace(/[/\\?%*:|"<>]/g, "_").trim()
}

/**
 * 같은 파일명이 둘 이상이면 확장자 앞에 " (2)", " (3)" 접미를 붙여 zip 내 덮어쓰기를 방지한다.
 * (같은 title 의 표 섹션 여러 개, 이름이 같은 액티비티 여러 개 등)
 */
export function dedupeFileNames(files: BatchCSVFile[]): BatchCSVFile[] {
  const seen = new Map<string, number>()

  return files.map((file) => {
    const count = seen.get(file.fileName) ?? 0
    seen.set(file.fileName, count + 1)
    if (count === 0) {
      return file
    }
    const dot = file.fileName.lastIndexOf(".")
    const base = dot === -1 ? file.fileName : file.fileName.slice(0, dot)
    const ext = dot === -1 ? "" : file.fileName.slice(dot)

    return { ...file, fileName: `${base} (${count + 1})${ext}` }
  })
}

// --- Daily: 액티비티별로 리포트 화면과 동일한 그룹 집계를 받아 Custom Table CSV 생성 ---
// Activity / All Sessions 모드 분기와 세션ID 확보는 fetchGroupSummary(호출부 클로저)가 담당한다.

export async function buildDailyBatchCSVFiles(params: {
  activities: { idfActivity: number; name: string }[]
  sections: StatsSectionSpec[]
  fetchGroupSummary: DailyGroupSummaryFetcher
  positionRankFns?: PositionRankFns
  metricMap: MetricMapType
  selectedUnitList?: Unit[]
  /** 커스텀 계산 메트릭 수식 — Custom Table 요약 행(#98)의 수식 재적용용. 없으면 해당 셀은 빈 값 */
  calculatedMetrics?: CalculatedMetric[]
  t: TFunction
  translateKey: (key: string) => string | undefined
  concurrency?: number
  onProgress?: (done: number, total: number) => void
}): Promise<BatchCSVFile[]> {
  const {
    activities,
    sections,
    fetchGroupSummary,
    positionRankFns,
    metricMap,
    selectedUnitList,
    calculatedMetrics,
    t,
    translateKey,
    concurrency = 3,
    onProgress,
  } = params

  const customTableSections = sections.filter((s) => s.sectionType === SECTION_TYPE.CUSTOM_TABLE)

  let processed = 0

  const perActivity = await mapWithConcurrency(activities, concurrency, async (activity) => {
    const files: BatchCSVFile[] = []

    for (const section of customTableSections) {
      const groupSummaryData = await fetchGroupSummary(activity.idfActivity, section.statsSettings)
      files.push({
        fileName: sanitizeFileName(`${activity.name}_${section.title}.csv`),
        content: buildCustomTableCSV({
          reportType: REPORT_TYPE.DAILY,
          statsSettings: section.statsSettings,
          groupSummaryData,
          metricMap,
          selectedUnitList,
          calculatedMetrics,
          positionRank: positionRankFns?.positionRank ?? (() => Number.MAX_SAFE_INTEGER),
          positionGroupRank: positionRankFns?.positionGroupRank ?? (() => Number.MAX_SAFE_INTEGER),
          t,
          translateKey,
        }),
      })
    }

    processed += 1
    onProgress?.(processed, activities.length)

    return files
  })

  return perActivity.flat()
}

// --- Trend: 섹션당 1회 (전체 액티비티 누적 집계) — 리포트 트렌드 Custom Table 과 동일 데이터/포맷 ---

export async function buildTrendBatchCSVFiles(params: {
  sections: StatsSectionSpec[]
  teamName: string
  metricMap: MetricMapType
  selectedUnitList?: Unit[]
  calculatedMetrics?: CalculatedMetric[]
  t: TFunction
  translateKey: (key: string) => string | undefined
  fetchGroupSummary: (settings: StatsSettings) => Promise<TrendGroupSummaryRow[]>
  positionRankFns?: PositionRankFns
  onProgress?: (done: number, total: number) => void
}): Promise<BatchCSVFile[]> {
  const {
    sections,
    teamName,
    metricMap,
    selectedUnitList,
    calculatedMetrics,
    t,
    translateKey,
    fetchGroupSummary,
    positionRankFns,
    onProgress,
  } = params

  let processed = 0

  const buildSectionContent = async (section: StatsSectionSpec) => {
    return buildCustomTableCSV({
      reportType: REPORT_TYPE.TREND,
      statsSettings: section.statsSettings,
      groupSummaryData: await fetchGroupSummary(section.statsSettings),
      metricMap,
      selectedUnitList,
      calculatedMetrics,
      positionRank: positionRankFns?.positionRank ?? (() => Number.MAX_SAFE_INTEGER),
      positionGroupRank: positionRankFns?.positionGroupRank ?? (() => Number.MAX_SAFE_INTEGER),
      t,
      translateKey,
    })
  }

  const files = await Promise.all(
    sections
      .filter((section) => section.sectionType === SECTION_TYPE.CUSTOM_TABLE)
      .map(async (section) => {
        const content = await buildSectionContent(section)
        processed += 1
        onProgress?.(processed, sections.length)

        return { fileName: sanitizeFileName(`Trend_${teamName}_${section.title}.csv`), content }
      }),
  )

  return files
}

// --- 압축 / 다운로드 ---

export async function zipCSVFiles(files: BatchCSVFile[]): Promise<Blob> {
  const zip = new JSZip()
  dedupeFileNames(files).forEach((file) => zip.file(file.fileName, BOM + file.content))

  return zip.generateAsync({ type: "blob" })
}

/** 파일이 1개면 단일 CSV, 2개 이상이면 zip 으로 다운로드 */
export async function downloadBatchCSV(files: BatchCSVFile[], zipBaseName: string): Promise<void> {
  if (files.length === 0) {
    return
  }

  if (files.length === 1) {
    const blob = new Blob([BOM, files[0].content], { type: "text/csv" })
    triggerBlobDownload(blob, files[0].fileName)

    return
  }

  const zipBlob = await zipCSVFiles(files)
  triggerBlobDownload(zipBlob, zipBaseName.endsWith(".zip") ? zipBaseName : `${zipBaseName}.zip`)
}
